import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// ---------------------------------------------------------------------------
// Prerender readiness signal
//
// The build-time prerenderer (supabase/functions/prerender) waits for
// `window.__PRERENDER_READY__ === true` before snapshotting the DOM. Without
// this signal Browserless raced our `useEffect` + Supabase fetches and
// captured empty grids on data-driven pages (Leadership Team, Blog, etc.).
//
// Strategy: wrap window.fetch and count in-flight requests to our Supabase
// REST/RPC endpoints. The page is "ready" once:
//   (a) at least one mount tick has happened, AND
//   (b) no Supabase request has been in flight for ≥250ms, AND
//   (c) <main> has non-trivial content.
// Hard fallback flips ready=true after 8s no matter what, so we never
// deadlock a route that legitimately has no Supabase calls.
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    __PRERENDER_READY__?: boolean;
    __PRERENDER_INFLIGHT__?: number;
    __PRERENDER_CLIENT_RENDERED__?: boolean;
  }
}

function PrerenderAwareApp() {
  useEffect(() => {
    window.__PRERENDER_CLIENT_RENDERED__ = true;
  }, []);
  return <App />;
}

(() => {
  if (typeof window === "undefined") return;
  window.__PRERENDER_READY__ = false;
  window.__PRERENDER_INFLIGHT__ = 0;
  window.__PRERENDER_CLIENT_RENDERED__ = false;
  const isPrerenderPass = new URLSearchParams(window.location.search).has("__prerender");

  // Browserless loads the live URL during publish builds. That URL may already
  // contain an older prerendered <main>, so clear it during the render pass;
  // otherwise the readiness probe can treat stale server HTML as fresh app DOM.
  if (isPrerenderPass) {
    document.getElementById("root")?.replaceChildren();
  }

  const supaHost = (() => {
    try {
      return new URL(import.meta.env.VITE_SUPABASE_URL as string).host;
    } catch {
      return "";
    }
  })();

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let isSupa = false;
    try {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : (input as Request).url;
      isSupa = !!supaHost && url.includes(supaHost);
    } catch {
      /* noop */
    }
    if (isSupa) window.__PRERENDER_INFLIGHT__ = (window.__PRERENDER_INFLIGHT__ ?? 0) + 1;
    const done = () => {
      if (isSupa) window.__PRERENDER_INFLIGHT__ = Math.max(0, (window.__PRERENDER_INFLIGHT__ ?? 1) - 1);
    };
    return originalFetch(input as RequestInfo, init).then(
      (r) => { done(); return r; },
      (e) => { done(); throw e; },
    );
  };

  // Mark ready when traffic settles AND <main> has content. Poll cheaply.
  let idleSince = 0;
  // Hard deadline must stay under prerender's READY_TIMEOUT_MS (20s).
  // Cluster pages fire several tag-driven Supabase fetches in sequence
  // (page → related content → resources) and 8s wasn't enough headroom.
  const HARD_DEADLINE_MS = 18000;
  const startedAt = Date.now();
  // Default <title> shipped in the static index.html shell. SEOHead replaces
  // it synchronously on every route, so for any non-root path we require the
  // title to have changed before declaring ready — otherwise the snapshot
  // would inherit the homepage <title>/canonical and look like a duplicate.
  const SHELL_TITLE = "Tango Networks";
  const isRoot = window.location.pathname === "/" || window.location.pathname === "";
  const tick = () => {
    if (window.__PRERENDER_READY__) return;
    const now = Date.now();
    const inflight = window.__PRERENDER_INFLIGHT__ ?? 0;
    if (inflight === 0) {
      if (idleSince === 0) idleSince = now;
      const mainEl = document.querySelector("main");
      const mainBytes = mainEl ? mainEl.innerHTML.length : 0;
      const titleReady = isRoot || document.title !== SHELL_TITLE;
      // Hosting serves the homepage snapshot as the SPA fallback for unknown
      // paths, so a snapshot taken before React mounts would inherit the
      // homepage head — the soft-404. Always require the client render.
      const clientRendered = window.__PRERENDER_CLIENT_RENDERED__ === true;
      // 404 / noindex routes have no <main>; their robots meta is the signal.
      const noIndexed = !!document.querySelector('meta[name="robots"]');
      if (now - idleSince >= 250 && (mainBytes > 200 || noIndexed) && titleReady && clientRendered) {
        window.__PRERENDER_READY__ = true;
        return;
      }
    } else {
      idleSince = 0;
    }
    if (now - startedAt >= HARD_DEADLINE_MS) {
      window.__PRERENDER_READY__ = true;
      return;
    }
    setTimeout(tick, 150);
  };
  // Start polling after first paint so we don't race the initial mount.
  setTimeout(tick, 200);
})();

const rootElement = document.getElementById("root");
if (rootElement) {
  createRoot(rootElement).render(<PrerenderAwareApp />);
}
